import type { RestartEvent, RestartHistory } from "../hooks/useRestartHistory";

export type TelemetryState = "reporting" | "stale" | "not_reporting";

export type GatewayTelemetry = {
  readonly state: TelemetryState;
  readonly httpHealthy: boolean | null;
  readonly serviceActive: boolean | null;
  readonly resourceSampleAvailable: boolean | null;
};

// The collector reports every minute; past 3 minutes its live signals say
// nothing about the gateway now, so they read as unknown.
export const FRESH_FOR_MS = 180_000;

export const gatewayTelemetry = (data: RestartHistory, now: number): GatewayTelemetry => {
  const age = data.lastSeen ? now - Date.parse(data.lastSeen) : Number.NaN;
  const fresh = Number.isFinite(age) && age >= -60_000 && age <= FRESH_FOR_MS;
  if (!fresh) {
    return {
      state: data.lastSeen ? "stale" : "not_reporting",
      httpHealthy: null,
      serviceActive: null,
      resourceSampleAvailable: null,
    };
  }
  return {
    state: data.telemetryStatus === "reporting" ? "reporting" : data.telemetryStatus === "stale" ? "stale" : "not_reporting",
    httpHealthy: data.httpHealthy ?? null,
    serviceActive: data.serviceActive ?? null,
    resourceSampleAvailable: data.resourceSampleAvailable ?? null,
  };
};

export const humanize = (value: string): string => value.replaceAll("_", " ");

export const formatDuration = (seconds: number): string => {
  const s = Math.round(seconds);
  if (s < 60) return `${s}s`;
  if (s < 3600) return `${Math.floor(s / 60)}m ${s % 60}s`;
  return `${Math.floor(s / 3600)}h ${Math.floor((s % 3600) / 60)}m`;
};

export type EventTone = "ok" | "warn" | "crit" | "muted";

// Outages and kills are red, anything nobody asked for is amber, a recovery is
// green, and planned starts and stops stay quiet.
export const eventTone = (event: Pick<RestartEvent, "kind" | "unexpected">): EventTone => {
  if (event.kind === "unavailable" || event.kind.includes("oom") || event.kind === "failed") return "crit";
  if (event.unexpected === true) return "warn";
  if (event.kind === "recovered") return "ok";
  return "muted";
};
